import { Injectable } from '@angular/core';
import { Observable, throwError, catchError, BehaviorSubject, tap } from 'rxjs';

import { TerraUtils } from 'src/app/shared/TerraUtils';

import { DistributionStatus } from 'src/app/shared/enums/DistributionStatus.enum';
import { DistributionTransactionPurpose } from '../../shared/enums/DistributionTransactionPurpose.enum';
import { CalculatePricingTierDistributionResult } from 'src/app/dashboard/shared/holding/distribution/CalculatePricingTierDistributionResult.model';
import { DistributionTableItemResponse } from 'src/app/dashboard/shared/holding/distribution/DistributionTableItemResponse.model';
import { DistributionTransferReqRes } from 'src/app/dashboard/shared/holding/distribution/DistributionTransferReqRes.model';
import { DistributionReqRes } from 'src/app/dashboard/shared/holding/distribution/DistributionReqRes.model';
import { UnfinalizedDistributionDTO } from '../../dashboard/shared/holding/distribution/UnfinalizedDistributionDTO.model';
import { SignatureResponse } from '../../shared/models/SignatureResponse.model';
import { ClientDetailsResponse } from 'src/app/account/my-account/model/agentDetailsResponse.model';
import { DistributionsImportResponse } from '../../shared/models/DistributionsImportResponse.model';
import { OrderExchangeRatesResponse } from 'src/app/shared/models/OrderExchangeRatesResponse.model';
import { SearchOptionsResponse } from 'src/app/shared/models/SearchOptionsResponse.model';
import { SearchOptionsRequest } from 'src/app/shared/models/SearchOptionsRequest.model';
import { PhoneVerificationReq } from '../../shared/components/phone-verification-dialog/PhoneVerificationReq.model';
import { InvestmentReqRes } from '../../dashboard/models/investment.model';
import { WaterfallsCreateDto } from '../../dashboard/shared/holding/distribution/WaterfallsCreateDto.model';
import { WaterfallsReadDto } from '../../dashboard/shared/holding/distribution/WaterfallsReadDto.model';
import { LastDistributionReqRes } from "../../dashboard/shared/holding/distribution/LastDistributionReqRes.model";
import { SendNoticeDto } from 'src/app/dashboard/shared/holding/distribution/sendNoticeDto.model';
import { FinalizeDistributionDto } from 'src/app/dashboard/shared/holding/distribution/finalizeDistributionDto.model';
import { MoveToCompletedDto } from 'src/app/dashboard/shared/holding/distribution/moveToCompletedDto.model';
import { DistributionDetailsItemReqRes } from '../../dashboard/shared/holding/distribution/import-distributions/distribution-details/DistributionDetailsItemReqRes';
import { HttpService } from '../http.service';

@Injectable({
  providedIn: 'root'
})
export class GpDistributionDataService {

  private _gpDistributionUpdated$ = new BehaviorSubject<void>(null)
  gpDistributionUpdated$ = this._gpDistributionUpdated$.asObservable()

  constructor(
    private http: HttpService) {
  }

  generateEndpointPrefix(holdingId: number) {
    return `holdings/${holdingId}/distributions`;
  }

  protected getSearchOptionsToQuerystring(options: SearchOptionsRequest): string {
    return `?filter=${options.filter ? options.filter : ''}&sortOrder=${options.sortOrder ? options.sortOrder : ''}&pageNumber=${options.pageNumber ? options.pageNumber : 0
      }&pageSize=${options.pageSize || 1000}&orderBy=${options.orderBy}`;
  }

  public getList(holdingId: number, options: SearchOptionsRequest): Observable<SearchOptionsResponse<DistributionTableItemResponse>> {
    const querystring = options ? this.getSearchOptionsToQuerystring(options) : '';
    return this.http.getTyped<SearchOptionsResponse<DistributionTableItemResponse>>(this.generateEndpointPrefix(holdingId) + querystring).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public getUnfinalized(): Observable<UnfinalizedDistributionDTO[]> {
    return this.http.getTyped<UnfinalizedDistributionDTO[]>("distributions/unfinalized").pipe(
      catchError(err => throwError(() => err))
    );
  }

  public create(holdingId: number, model: DistributionReqRes): Observable<DistributionReqRes> {
    return this.http.postTyped<DistributionReqRes>(this.generateEndpointPrefix(holdingId), model).pipe(
      tap(() => this._gpDistributionUpdated$.next()),
      catchError(err => throwError(() => err))
    );
  }

  public update(holdingId: number, distributionId: number, model: DistributionReqRes): Observable<DistributionReqRes> {
    return this.http.putTyped<DistributionReqRes>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}`, model).pipe(
      tap(() => this._gpDistributionUpdated$.next()),
      catchError(err => throwError(() => err))
    );
  }

  public partialUpdate(holdingId: number, distributionId: number, model: DistributionReqRes): Observable<DistributionReqRes> {
    return this.http.putTyped<DistributionReqRes>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/partial`, model).pipe(
      tap(() => this._gpDistributionUpdated$.next()),
      catchError(err => throwError(() => err))
    );
  }

  public delete(holdingId: number, distributionId: number): Observable<void> {
    return this.http.deleteTyped<void>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}`).pipe(
      tap(() => this._gpDistributionUpdated$.next()),
      catchError(err => throwError(() => err))
    );
  }

  public getById(holdingId: number, distributionId: number): Observable<DistributionReqRes> {
    return this.http.getTyped<DistributionReqRes>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}`).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public getLastDistributionForHolding(holdingId: number): Observable<LastDistributionReqRes> {
    return this.http.getTyped<LastDistributionReqRes>(`${this.generateEndpointPrefix(holdingId)}/last`).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public getDistributionTemplate(holdingId: number, totalProrata: number = 0, reasonsForTransfer: DistributionTransactionPurpose[]): Observable<string> {
    return this.http.postTyped<string>(`${this.generateEndpointPrefix(holdingId)}/template?totalProrata=${totalProrata}`, reasonsForTransfer)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public getDistributionTransfersFromExcelFile(holdingId: number, metaFileLinkId: number): Observable<DistributionTransferReqRes[]> {
    return this.http.getTyped<DistributionTransferReqRes[]>(`${this.generateEndpointPrefix(holdingId)}/template/${metaFileLinkId}`)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public getMultiDistributionsTemplate(holdingId: number, distributionsDetails: DistributionDetailsItemReqRes[]): Observable<string> {
    return this.http.postTyped<string>(`${this.generateEndpointPrefix(holdingId)}/multitemplate`, distributionsDetails)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public getMultiDistributionsImportFromExcelFile(holdingId: number, metaFileLinkId: number): Observable<DistributionsImportResponse> {
    return this.http.getTyped<DistributionsImportResponse>(`${this.generateEndpointPrefix(holdingId)}/multitemplate/${metaFileLinkId}`)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public getTransfersForHolding(holdingId: number): Observable<DistributionTransferReqRes[]> {
    return this.http.getTyped<DistributionTransferReqRes[]>(`${this.generateEndpointPrefix(holdingId)}/transfers`)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public createDistributionTransfer(holdingId: number, distributionId: number, distributionTransferReqRes: DistributionTransferReqRes): Observable<DistributionTransferReqRes> {
    return this.http.postTyped<DistributionTransferReqRes>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/transfers`,
      distributionTransferReqRes).pipe(
        tap(() => this._gpDistributionUpdated$.next()),
        catchError(err => throwError(() => err))
      );
  }

  public updateDistributionTransfer(holdingId: number, distributionId: number, distributionTransferReqRes: DistributionTransferReqRes): Observable<DistributionTransferReqRes> {
    // temp hack for bank account to pass validation
    distributionTransferReqRes = <DistributionTransferReqRes>({ ...distributionTransferReqRes, clientBankAccount: null });
    return this.http.putTyped<DistributionTransferReqRes>(
      `${this.generateEndpointPrefix(holdingId)}/${distributionId}/transfers/${distributionTransferReqRes.id}`,
      distributionTransferReqRes)
      .pipe(
        tap(() => this._gpDistributionUpdated$.next()),
        catchError(err => throwError(() => err))
      );
  }

  public distributionNotice(holdingId: number, distributionId: number, personalMessage: string = null, metaFileLinkIds?: number[]): Observable<DistributionReqRes> {
    const model = new SendNoticeDto();
    model.distributionId = distributionId;
    model.noticePersonalMessage = personalMessage;
    model.noticeDate = this.currentDate;
    if (metaFileLinkIds) {
      model.metaFileLinkIds = metaFileLinkIds;
    }

    return this.http.putTyped<DistributionReqRes>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/notice`,
      model).pipe(
        tap(() => this._gpDistributionUpdated$.next()),
        catchError(err => throwError(() => err))
      );
  }

  public distributionPayout(holdingId: number, distributionId: number, personalMessage: string = null, sendEmail: boolean, metaFileLinkIds?: number[]): Observable<DistributionReqRes> {
    const model = new FinalizeDistributionDto();
    model.distributionId = distributionId;
    model.payoutPersonalMessage = personalMessage;
    model.sendEmail = sendEmail;
    model.finalizeDate = this.currentDate;
    if (metaFileLinkIds) {
      model.metaFileLinkIds = metaFileLinkIds;
    }

    return this.http.putTyped<DistributionReqRes>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/payout`,
      model).pipe(
        tap(() => this._gpDistributionUpdated$.next()),
        catchError(err => throwError(() => err))
      );
  }

  public ApproveBankAccountForDistributionTransfer(holdingId: number, distributionId: number, distributionTransferId: number): Observable<void> {
    const url = `${this.generateEndpointPrefix(holdingId)}/${distributionId}/transfer/${distributionTransferId}/approve-bank-account`;
    return this.http.postTyped<void>(url, {}).pipe(
      tap(() => this._gpDistributionUpdated$.next()),
      catchError(err => throwError(() => err))
    );
  }

  public distributionUsers(holdingId: number): Observable<ClientDetailsResponse[]> {
    return this.http.getTyped<ClientDetailsResponse[]>(`${this.generateEndpointPrefix(holdingId)}/users`).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public calculateFees(holdingId: number, model: DistributionReqRes): Observable<CalculatePricingTierDistributionResult> {
    const submitModel = new DistributionReqRes();
    submitModel.distributionTransfers = model.distributionTransfers;
    submitModel.distributionSourceCurrencyId = model.distributionSourceCurrencyId;
    submitModel.unitBankAccountId = model.unitBankAccountId;
    return this.http.postTyped<CalculatePricingTierDistributionResult>(`${this.generateEndpointPrefix(holdingId)}/fees`, submitModel)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public moveToCompleted(holdingId: number, distributionId: number, completedDate: Date = this.currentDate, forced: boolean = false): Observable<DistributionStatus> {
    const model = { distributionId, completedDate: completedDate, forced } as MoveToCompletedDto;
    return this.http.putTyped<DistributionStatus>(
      `${this.generateEndpointPrefix(holdingId)}/${distributionId}/mark-completed`, model)
      .pipe(
        tap(() => this._gpDistributionUpdated$.next()),
        catchError(err => throwError(() => err))
      );
  }

  public getPricingForTransfer(holdingId: number, distributionId: number, distributionTransferId: number): Observable<OrderExchangeRatesResponse> {
    const url = `${this.generateEndpointPrefix(holdingId)}/${distributionId}/transfers/${distributionTransferId}/pricing`;
    return this.http.getTyped<OrderExchangeRatesResponse>(url)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public deleteTransfer(distributionId: number, transferId: number) {
    const url = `holdings/distributions/${distributionId}/transfers/${transferId}`;
    return this.http.deleteTyped<boolean>(url)
      .pipe(
        tap(() => this._gpDistributionUpdated$.next()),
        catchError(err => throwError(() => err))
      );
  }

  public getSignatures(distributionId: number): Observable<SignatureResponse[]> {
    const url = `holdings/distributions/signatures/${distributionId}`;
    return this.http.getTyped<any[]>(url)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public markSignatureSigned(distributionId: number): Observable<boolean> {
    const url = `holdings/distributions/signatures/sign/${distributionId}`;
    return this.http.putTyped<boolean>(url)
      .pipe(
        catchError(err => throwError(() => err))
      );
  }

  public exportDistributions(fileType: 'excel' | 'csv', holdingId: number = null, distributionId: number = null): Observable<string> {
    const queryParams = [];

    queryParams.push(`fileType=${fileType}`);

    if (holdingId) {
      queryParams.push(`holdingId=${holdingId}`);
    }
    if (distributionId) {
      queryParams.push(`distributionId=${distributionId}`);
    }

    const queryParamsStr = queryParams.join('&');

    return this.http.getTyped<string>(`distributions/export` + (queryParams.length ? `?${queryParamsStr}` : '')).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public SetPhoneVerification(holdingId: number, distributionId: number) {
    return this.http.putTyped<any>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/phone-verification`).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public sendVerificationCode(holdingId: number, distributionId: number) {
    return this.http.getTyped<any>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/phone-verification/send-code`).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public verifyPhone(holdingId: number, distributionId: number, request: PhoneVerificationReq) {
    return this.http.postTyped<any>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/phone-verification/verify`, request).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public addInvestorsOutOfDistribution(holdingId: number, distributionId: number, investingEntitiesIds: number[]) {
    return this.http.postTyped<InvestmentReqRes[]>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/addInvestorsOutOfDistribution`, investingEntitiesIds).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public generateWaterfalls(holdingId: number, waterfallsRequest: WaterfallsCreateDto): Observable<WaterfallsReadDto> {
    return this.http.postTyped<WaterfallsReadDto>(`${this.generateEndpointPrefix(holdingId)}/waterfalls`, waterfallsRequest).pipe(
      catchError(err => throwError(() => err))
    );
  }

  public distributionChangedSinceWaterfallCalculation(holdingId: number, distributionId: number): Observable<boolean> {
    return this.http.getTyped<boolean>(`${this.generateEndpointPrefix(holdingId)}/${distributionId}/waterfalls/update-required`).pipe(
      catchError(err => throwError(() => err))
    );
  }

  private get currentDate(): Date {
    return TerraUtils.forceUtc(new Date(new Date().toDateString()))
  }
}
